import { applyScopedCustomCss } from "../shared/appearance";
import { CUSTOM_CSS_ATTRIBUTE, NOTICE_ID_ATTRIBUTE } from "./constants";

/**
 * Injects the admin's custom CSS scoped to one notice, the way the cookie
 * banner scopes its own: `@scope` keeps every rule inside the elements that
 * carry this notice's id (the dialog, its overlay and its confirm step), so
 * nothing reaches the host page. Goes into the shadow root the notice renders
 * in. Returns the cleanup that removes it.
 */
export const applyNoticeCustomCss = (
  root: Document | ShadowRoot,
  noticeId: string,
  css: string,
): (() => void) =>
  applyScopedCustomCss({
    root,
    scopeAttribute: NOTICE_ID_ATTRIBUTE,
    scopeId: noticeId,
    markerAttribute: CUSTOM_CSS_ATTRIBUTE,
    css,
  });

/**
 * Keeps one notice's custom CSS injected while it changes (a language switch,
 * a dark device, a later notice read), re-injecting only when the CSS does.
 */
export class NoticeCustomCss {
  private css: string | undefined;
  private cleanup: (() => void) | null = null;

  apply(root: Document | ShadowRoot, noticeId: string, css: string | undefined): void {
    if (css === this.css && (this.cleanup || !css)) return;
    this.remove();
    this.css = css;
    if (css) this.cleanup = applyNoticeCustomCss(root, noticeId, css);
  }

  remove(): void {
    this.cleanup?.();
    this.cleanup = null;
    this.css = undefined;
  }
}
